import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateCurrentUser } from '../../../../../actions/userActions';
import { createLoadingSelector } from '../../../../../apis/selectors';
import { useToggle } from '../../../../../hooks/useToggle';
import { useUserPermanentJobList } from '../../../PermanentJobV2/UserPermanentJobListV2/hooks/useUserPermanentJobList';
import WarningAlertIcon from '../../../../../images/warning_alert_outline_yellow.svg';
import { Colors } from '../../../../../themes/colors';
import CustomPortal from '../../../../commonComponents/CustomPortal';

export const RequestControl = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.users.currentUser);

  const loadingSelector = createLoadingSelector(['UPDATE_CURRENT_USER']);
  const isLoading = useSelector((state) => loadingSelector(state));

  const [allowedRequest, setAllowedRequest] = useState(currentUser?.allowedRequest || {});
  const [isShowTurnOffPermProductConfirmationPopup, controls] = useToggle(false);

  // Sync local state with Redux store when currentUser changes
  useEffect(() => {
    if (currentUser?.allowedRequest) {
      setAllowedRequest(currentUser.allowedRequest);
    }
  }, [currentUser?.allowedRequest, currentUser?.id]); // Include id to handle user switches

  const { data: permanentJobs } = useUserPermanentJobList(currentUser?.id ?? null);

  const hasJobPostings = permanentJobs && permanentJobs?.length > 0;

  const handleTempShiftsChange = () => {
    setAllowedRequest({
      ...allowedRequest,
      tempOrMdbShifts: !allowedRequest?.tempOrMdbShifts,
    });
  };

  const handlePermanentPlacementChange = () => {
    setAllowedRequest({
      ...allowedRequest,
      ppJob: !allowedRequest?.ppJob,
      permProduct: false, // Reset perm product when PP is toggled off
    });
  };

  const handlePermProductChange = () => {
    setAllowedRequest({
      ...allowedRequest,
      permProduct: !allowedRequest?.permProduct,
    });
  };

  const handleLocumTenensChange = () => {
    setAllowedRequest({
      ...allowedRequest,
      locumTenensDentist: !allowedRequest?.locumTenensDentist,
    });
  };

  const handleSave = () => {
    // Check if turning off perm product when it was previously enabled
    if (!allowedRequest?.permProduct && currentUser?.allowedRequest?.permProduct) {
      // Check if there are active jobs
      if (hasJobPostings) {
        // Show confirmation modal only if there are active jobs
        controls.setOn();
        return;
      }
      // No active jobs, proceed directly with the update
    }

    dispatch(updateCurrentUser({ allowedRequest }));
  };

  const handleConfirmTurnOffPermProduct = () => {
    dispatch(updateCurrentUser({ allowedRequest }));
    controls.setOff();
  };

  const handleCancelTurnOffPermProduct = () => {
    setAllowedRequest(currentUser?.allowedRequest || {});
    controls.setOff();
  };

  return (
    <>
      <div className="card mb-2">
        <div
          className="card-body"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignContent: 'center',
          }}
        >
          <h3 className="card-title" style={{ textAlign: 'center' }}>
            Request Controls
          </h3>

          <div style={{ display: 'flex', alignItems: 'center', marginTop: 15 }}>
            <input
              className="react-switch-checkbox mr-2"
              style={{ margin: 0 }}
              type="checkbox"
              onChange={handleTempShiftsChange}
              checked={allowedRequest?.tempOrMdbShifts}
            />

            <p style={{ marginBottom: 0 }}>Able to request Temp Shifts</p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center' }}>
            <input
              className="react-switch-checkbox mr-2"
              style={{ margin: 0 }}
              type="checkbox"
              onChange={handlePermanentPlacementChange}
              checked={allowedRequest?.ppJob}
            />

            <p style={{ marginBottom: 0 }}>Able to request Permanent Placement</p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', marginLeft: 16 }}>
            <input
              className="react-switch-checkbox mr-2"
              style={{ margin: 0 }}
              type="checkbox"
              disabled={!allowedRequest?.ppJob}
              onChange={handlePermProductChange}
              checked={allowedRequest?.permProduct}
            />

            <p style={{ marginBottom: 0, opacity: !allowedRequest?.ppJob ? 0.4 : 1 }}>
              Able to use Perm Product
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center' }}>
            <input
              className="react-switch-checkbox mr-2"
              style={{ margin: 0 }}
              type="checkbox"
              onChange={handleLocumTenensChange}
              checked={allowedRequest?.locumTenensDentist}
            />

            <p style={{ marginBottom: 0 }}>Able to request Locum Tenens Dentist</p>
          </div>
        </div>

        <button
          type="button"
          className="btn btn-success"
          style={{
            alignSelf: 'center',
            marginBottom: 10,
          }}
          onClick={handleSave}
          disabled={isLoading}
        >
          Save
        </button>
      </div>

      {/* Confirmation Modal for turning off Perm Product */}
      {isShowTurnOffPermProductConfirmationPopup && (
        <CustomPortal
          Icon={WarningAlertIcon}
          title="Before You Continue"
          cancelText="Cancel"
          submitText="Confirm"
          containerStyle={{ paddingInline: 50, paddingTop: 30 }}
          textStyle={{ textAlign: 'center' }}
          cancelPressed={handleCancelTurnOffPermProduct}
          submitPressed={handleConfirmTurnOffPermProduct}
        >
          <p style={{ color: Colors.neutral_500, fontSize: 16, textAlign: 'center' }}>
            This office has active jobs. Disabling this toggle will cancel all active jobs and
            interviews. A CS member will process refunds and notify the office.
          </p>
        </CustomPortal>
      )}
    </>
  );
};
